import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
  FileDown,
  FilePen,
  FilePlus,
  FileUp,
  LayoutDashboard,
  type LucideIcon,
  Send,
  UserCog,
  Users,
} from 'lucide-react';
import { AccountMenu } from '@/components/layout/AccountMenu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import type { Me } from '@/features/auth/api';
import { DraftsNavBadge, SentNavBadge } from '@/features/invoices/badges/NavBadges';

interface NavLink {
  id: string;
  to: string;
  icon: LucideIcon;
  /** Optional adornment rendered as a sibling of the nav button (e.g. the drafts count badge). */
  badge?: ComponentType;
}

/** KSeF read side — invoices synced from / registered in KSeF: dashboard, issued, received. */
const KSEF_LINKS: NavLink[] = [
  { id: 'summary', to: '/invoices/summary', icon: LayoutDashboard },
  { id: 'issued', to: '/invoices/issued', icon: FileUp },
  { id: 'received', to: '/invoices/received', icon: FileDown },
];

/** Write side — the invoice composer + the outbound register; shown only when issuing is enabled. */
const ISSUING_LINKS: NavLink[] = [
  { id: 'new', to: '/invoices/new', icon: FilePlus },
  { id: 'drafts', to: '/invoices/drafts', icon: FilePen, badge: DraftsNavBadge },
  { id: 'sent', to: '/invoices/sent', icon: Send, badge: SentNavBadge },
];

/**
 * App navigation: brand, the invoice nav groups, and the account control pinned to the footer. Wraps
 * shadcn's `Sidebar`, which renders a persistent rail on desktop (collapsible to icons via the rail,
 * the trigger, or ⌘B) and an off-canvas drawer on mobile.
 *
 * Routes are flat icon items under a group label, so they stay reachable (as icons) when the rail
 * collapses; the label itself hides in icon mode.
 */
export function AppSidebar({ me }: { me: Me }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { setOpenMobile } = useSidebar();
  const isActive = (to: string) => pathname === to || pathname.startsWith(`${to}/`);
  const closeMobile = () => setOpenMobile(false);

  const renderInvoiceGroup = (labelKey: string, links: NavLink[]) => (
    <SidebarGroup>
      <SidebarGroupLabel>{t(labelKey)}</SidebarGroupLabel>
      <SidebarMenu>
        {links.map((link) => {
          const active = isActive(link.to);
          return (
            <SidebarMenuItem key={link.id}>
              <SidebarMenuButton
                asChild
                isActive={active}
                tooltip={t(`invoices.heading.${link.id}`)}
              >
                <Link to={link.to} aria-current={active ? 'page' : undefined} onClick={closeMobile}>
                  <link.icon />
                  <span>{t(`nav.${link.id}`)}</span>
                </Link>
              </SidebarMenuButton>
              {link.badge ? <link.badge /> : null}
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex h-8 items-center px-2 font-semibold tracking-tight group-data-[collapsible=icon]:hidden">
          kstefan
        </div>
      </SidebarHeader>
      <SidebarContent>
        {renderInvoiceGroup('nav.invoices', KSEF_LINKS)}
        {me.issuingEnabled ? renderInvoiceGroup('nav.create_invoices', ISSUING_LINKS) : null}
        <SidebarGroup>
          <SidebarGroupLabel>{t('nav.others')}</SidebarGroupLabel>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={isActive('/customers')}
                tooltip={t('customers.title')}
              >
                <Link
                  to="/customers"
                  aria-current={isActive('/customers') ? 'page' : undefined}
                  onClick={closeMobile}
                >
                  <Users />
                  <span>{t('nav.customers')}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
        {me.role === 'ADMIN' && (
          <SidebarGroup className="mt-auto">
            <SidebarGroupLabel>{t('nav.administration')}</SidebarGroupLabel>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isActive('/admin/users')}
                  tooltip={t('admin.users.title')}
                >
                  <Link
                    to="/admin/users"
                    aria-current={isActive('/admin/users') ? 'page' : undefined}
                    onClick={closeMobile}
                  >
                    <UserCog />
                    <span>{t('nav.users')}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        )}
      </SidebarContent>
      <SidebarFooter>
        <AccountMenu me={me} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
